import React, { useState, useEffect, useCallback, useRef } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge, stripAnswerLeak } from '../lib/GameVisuals';
import { Blocks, Undo2, ChevronRight, CheckCircle, XCircle, Zap, Lightbulb } from 'lucide-react';

/* ─── Tutorial Screen ─── */
function TutorialScreen({ onStart, items }) {
  const firstItem = items[0];
  const sampleEnglish = firstItem?.english_meaning || 'I have brown hair.';
  const sampleWords = firstItem?.scrambled_words || ['cheveux', 'les', 'bruns.', 'ai', "J'"];
  const sampleOrder = firstItem?.correct_order || ["J'", 'ai', 'les', 'cheveux', 'bruns.'];
  const hasHints = items.some(i => i.hint);

  return (
    <div data-testid="batisseur-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #FFF9DB 0%, #FFF3BF 40%, #F0F4F8 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 760, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Blocks size={48} color="#F59F00" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            BATISSEUR
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Build French sentences word by word!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {/* Card 1: Tap words */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(76,110,245,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <CheckCircle size={40} color="#4C6EF5" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#4C6EF5', marginBottom: '0.3rem' }}>
              Build
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              TAP words in the correct order to build the French sentence
            </div>
          </div>

          {/* Card 2: Wrong = shake */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(224,49,49,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <XCircle size={40} color="#E03131" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#E03131', marginBottom: '0.3rem' }}>
              Oops!
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Wrong word? It shakes! Try again with the correct one
            </div>
          </div>

          {/* Card 3: Streak */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Zap size={40} color="#F59F00" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Streak!
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Build sentences perfectly to keep your streak going!
            </div>
          </div>
        </div>

        {/* Example sentence */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: '#555', marginBottom: '0.5rem' }}>
            Example:
          </div>
          <div style={{
            display: 'inline-block', padding: '0.4rem 1.2rem', borderRadius: '0.75rem',
            background: 'rgba(255,255,255,0.9)', border: '2px solid rgba(245,159,0,0.2)',
            fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 600, color: '#555', marginBottom: '0.75rem',
          }}>
            {sampleEnglish}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {sampleOrder.map((w, i) => (
              <span key={i} style={{
                padding: '0.35rem 0.9rem', borderRadius: '0.75rem',
                background: '#4C6EF5', color: 'white',
                fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 700,
              }}>
                {i + 1}. {w}
              </span>
            ))}
          </div>
          {hasHints && (
            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
              <Lightbulb size={18} color="#F59F00" />
              <span style={{ fontFamily: 'Nunito', fontSize: '0.95rem', color: '#888', fontWeight: 600 }}>
                Some sentences include hints to help you
              </span>
            </div>
          )}
        </div>

        <button data-testid="batisseur-tutorial-start" onClick={onStart} className="btn-arcade btn-primary"
          style={{ fontSize: '1.8rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem', borderBottomWidth: 8 }}>
          <ChevronRight size={32} /> Let's Build!
        </button>
      </div>
    </div>
  );
}

/* ─── Set Transition Screen ─── */
function SetTransition({ sentenceNum, totalSentences, englishMeaning, wordCount, hint, onReady }) {
  const [countdown, setCountdown] = useState(3);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          // Use setTimeout to avoid setState during render
          setTimeout(() => onReadyRef.current(), 0);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="batisseur-set-transition" style={{
      position: 'absolute', inset: 0, zIndex: 50,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '1.5rem', padding: '2rem 3rem',
        textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        animation: 'popIn 0.3s ease-out', maxWidth: 600,
      }}>
        <style>{`@keyframes popIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.3rem' }}>
          SENTENCE {sentenceNum} of {totalSentences}
        </div>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.8rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.5rem',
        }}>
          {englishMeaning || 'Build the sentence!'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
          <span style={{
            padding: '0.2rem 0.75rem', borderRadius: '2rem',
            background: 'rgba(76,110,245,0.1)', fontFamily: 'Fredoka', fontSize: '1rem',
            fontWeight: 700, color: '#4C6EF5',
          }}>
            {wordCount} words
          </span>
        </div>
        {hint && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
            marginTop: '0.25rem', padding: '0.3rem 0.75rem', borderRadius: '0.75rem',
            background: 'rgba(245,159,0,0.06)',
          }}>
            <Lightbulb size={16} color="#F59F00" />
            <span style={{ fontFamily: 'Nunito', fontSize: '0.95rem', color: '#888', fontWeight: 600, fontStyle: 'italic' }}>
              {hint}
            </span>
          </div>
        )}
        <div style={{ marginTop: '1rem', fontFamily: 'Fredoka', fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary)' }}>
          {countdown}
        </div>
      </div>
    </div>
  );
}

export function Batisseur({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('batisseur'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const batItems = items.filter(i => i.scrambled_words && i.correct_order);
  const maxRounds = Math.min(settings.rounds, batItems.length);

  const [showTutorial, setShowTutorial] = useState(true);
  const [showTransition, setShowTransition] = useState(false);
  const [roundIdx, setRoundIdx] = useState(0);
  const [placed, setPlaced] = useState([]);
  const [available, setAvailable] = useState([]);
  const [score, setScore] = useState(0);
  const [errors, setErrors] = useState(0);
  const [feedback, setFeedback] = useState(null); // null | 'complete' | 'wrong'
  const [wrongWord, setWrongWord] = useState(null); // the word that should have been next
  const [showHint, setShowHint] = useState(false); // user must click to see hint
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [perfectSentence, setPerfectSentence] = useState(true); // no errors in current sentence
  const [showConfetti, setShowConfetti] = useState(0);
  const scoreRef = useRef(null); // DOM ref for animation
  const streakRef = useRef(null); // DOM ref for animation
  const sentenceRef = useRef(null);
  const endedRef = useRef(false);
  const reviewRef = useRef([]);
  // Value refs to avoid stale closures in callbacks
  const scoreValRef = useRef(0);
  const bestStreakValRef = useRef(0);
  const roundIdxRef = useRef(0);
  scoreValRef.current = score;
  bestStreakValRef.current = bestStreak;
  roundIdxRef.current = roundIdx;

  const currentItem = batItems[roundIdx];

  // Initialize available words when round changes
  useEffect(() => {
    if (!currentItem) return;
    // Fisher-Yates shuffle for uniform randomness
    const words = [...currentItem.scrambled_words];
    for (let i = words.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [words[i], words[j]] = [words[j], words[i]];
    }
    setAvailable(words);
    setPlaced([]);
    setFeedback(null);
    setWrongWord(null);
    setShowHint(false);
    setPerfectSentence(true);
  }, [roundIdx]);

  const handleTutorialDone = useCallback(() => {
    setShowTutorial(false);
  }, []);

  const handleTransitionDone = useCallback(() => {
    setShowTransition(false);
  }, []);

  // endGame must be defined before advanceToNext to avoid hoisting issues
  const endGame = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    setShowConfetti(c => c + 1);
    playSound('win');
    const finalScore = scoreValRef.current;
    const finalRound = roundIdxRef.current;
    const finalBestStreak = bestStreakValRef.current;
    // Add unanswered sentences
    const review = [...reviewRef.current];
    for (let i = review.length; i < maxRounds && i < batItems.length; i++) {
      const it = batItems[i]; if (!it) break;
      review.push({ english: it.english_meaning || '', correctWords: [...(it.correct_order || [])], wasCorrect: null });
    }
    setTimeout(() => onGameEnd({
      team1Score: finalScore, team2Score: 0,
      totalQuestions: finalRound + 1,
      accuracy: (finalRound + 1) > 0 ? (finalScore / (finalRound + 1)) * 100 : 0,
      bestStreak: finalBestStreak,
      questionReview: review, reviewType: 'sentence',
    }), 1500);
  }, [onGameEnd, playSound]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => endGame();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [endGame]);

  const advanceToNext = useCallback(() => {
    const next = roundIdxRef.current + 1;
    if (next >= maxRounds) {
      endGame();
    } else {
      setRoundIdx(next);
    }
  }, [maxRounds, endGame]);

  const handleWordTap = useCallback((word, idx) => {
    if (feedback) return;
    playSound('click');
    const correctOrder = currentItem.correct_order;
    const nextPos = placed.length;

    if (word === correctOrder[nextPos]) {
      const newPlaced = [...placed, word];
      setPlaced(newPlaced);
      setAvailable(prev => prev.filter((_, i) => i !== idx));

      // Animate word placement
      if (sentenceRef.current) {
        const words = sentenceRef.current.children;
        if (words[nextPos]) {
          gsap.fromTo(words[nextPos], { scale: 0.5, opacity: 0, y: 10 }, { scale: 1, opacity: 1, y: 0, duration: 0.3, ease: 'back.out(2)' });
        }
      }

      // Sentence complete
      if (newPlaced.length === correctOrder.length) {
        playSound('match');
        setScore(s => s + 1);
        setFeedback('complete');
        if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.4 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
        // Track for review
        reviewRef.current.push({
          english: currentItem.english_meaning || '',
          correctWords: [...correctOrder],
          wasCorrect: perfectSentence,
        });

        // Streak: only increment if no errors in this sentence
        if (perfectSentence) {
          setStreak(s => {
            const newStreak = s + 1;
            setBestStreak(b => Math.max(b, newStreak));
            return newStreak;
          });
          if (streakRef.current) gsap.fromTo(streakRef.current, { scale: 1.5 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
        } else {
          setStreak(0);
        }

        setTimeout(() => advanceToNext(), 1400);
      }
    } else {
      playSound('wrong');
      setErrors(e => e + 1);
      setPerfectSentence(false);

      // Store the correct word but don't reveal it
      setWrongWord(correctOrder[nextPos]);
      setFeedback('wrong');
      setShowHint(false);

      // Shake the tapped word
      const el = document.querySelector(`[data-testid="bat-word-${idx}"]`);
      if (el) gsap.fromTo(el, { x: 0 }, { x: 10, duration: 0.06, repeat: 4, yoyo: true });

      setTimeout(() => {
        setFeedback(null);
      }, 800);
    }
  }, [feedback, currentItem, placed, perfectSentence, playSound, advanceToNext]);

  const handleUndo = useCallback(() => {
    if (placed.length === 0 || feedback) return;
    const lastWord = placed[placed.length - 1];
    setAvailable(prev => [...prev, lastWord]);
    setPlaced(prev => prev.slice(0, -1));
    playSound('click');
  }, [placed, feedback, playSound]);

  if (!currentItem && !showTutorial) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text-muted)', fontSize: '1.5rem', fontFamily: 'Fredoka',
      }}>
        No sentence content available
      </div>
    );
  }

  const progress = maxRounds > 0 ? ((roundIdx) / maxRounds) * 100 : 0;

  return (
    <div data-testid="batisseur-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #FFF9DB 0%, #F0F4F8 50%, #E8EDF4 100%)',
      position: 'relative',
    }}>
      <Confetti trigger={showConfetti} />

      {showTutorial && <TutorialScreen onStart={handleTutorialDone} items={batItems.slice(0, maxRounds)} />}

      {showTransition && currentItem && (
        <SetTransition
          sentenceNum={roundIdx + 1}
          totalSentences={maxRounds}
          englishMeaning={currentItem.english_meaning}
          wordCount={currentItem.correct_order.length}
          hint={currentItem.hint}
          onReady={handleTransitionDone}
        />
      )}

      <GameHeader title="BATISSEUR" icon={<Blocks size={24} color="#F59F00" />} color="#F59F00">
        <ScoreBadge label="BUILT" value={`${score}/${maxRounds}`} color="var(--success)" scoreRef={scoreRef} />
        {streak >= 2 && (
          <div ref={streakRef} data-testid="streak-badge" style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: 'rgba(245,159,0,0.1)', padding: '0.2rem 0.75rem', borderRadius: '2rem',
            border: '2px solid rgba(245,159,0,0.3)',
          }}>
            <Zap size={18} color="#F59F00" fill="#F59F00" />
            <span style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 800, color: '#F59F00' }}>{streak}</span>
          </div>
        )}
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: 'var(--text-muted)', fontWeight: 600 }}>
          {roundIdx + 1}/{maxRounds}
        </div>
      </GameHeader>

      {/* Progress bar */}
      <div style={{ height: 6, background: 'rgba(0,0,0,0.06)' }}>
        <div style={{
          height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #F59F00, #FFD43B)',
          transition: 'width 0.5s ease', borderRadius: '0 3px 3px 0',
        }} />
      </div>

      {currentItem && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: '1.25rem', padding: '1rem 1.5rem',
        }}>
          {/* English meaning — large, prominent */}
          {currentItem.english_meaning && (
            <div data-testid="english-meaning" style={{
              fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, fontFamily: 'Fredoka',
              color: 'var(--text-primary)', textAlign: 'center',
              background: 'rgba(255,255,255,0.95)', padding: '0.75rem 2rem', borderRadius: '1.25rem',
              border: '2px solid rgba(245,159,0,0.2)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              maxWidth: 850,
            }}>
              {stripAnswerLeak(currentItem.english_meaning, currentItem.completed_sentence || (currentItem.correct_order || []).join(' '))}
            </div>
          )}

          {/* Hint */}
          {currentItem.hint && placed.length === 0 && (
            <div data-testid="hint-display" style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.35rem 1rem', borderRadius: '2rem',
              background: 'rgba(245,159,0,0.08)', border: '1.5px solid rgba(245,159,0,0.15)',
            }}>
              <Lightbulb size={18} color="#F59F00" />
              <span style={{ fontFamily: 'Nunito', fontSize: '1.1rem', color: '#888', fontWeight: 600, fontStyle: 'italic' }}>
                {currentItem.hint}
              </span>
            </div>
          )}

          {/* Sentence building area */}
          <div style={{ width: '100%', maxWidth: 900, textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 700, fontFamily: 'Fredoka' }}>
              Build the sentence ({placed.length}/{currentItem.correct_order.length})
            </div>
            <div ref={sentenceRef} data-testid="sentence-area" style={{
              minHeight: 80, padding: '1rem 1.25rem', borderRadius: '1.25rem',
              background: 'rgba(255,255,255,0.95)',
              border: feedback === 'complete' ? '3px solid var(--success)'
                : feedback === 'wrong' ? '3px solid rgba(224,49,49,0.4)'
                : '2px solid rgba(0,0,0,0.08)',
              display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              transition: 'border-color 0.3s',
            }}>
              {placed.map((word, i) => (
                <span key={i} style={{
                  padding: '0.5rem 1.2rem', borderRadius: '0.75rem',
                  background: 'var(--primary)', color: 'white',
                  fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', fontWeight: 700, fontFamily: 'Fredoka',
                  boxShadow: '0 2px 8px rgba(76,110,245,0.3)',
                }}>{word}</span>
              ))}
              {placed.length < currentItem.correct_order.length && (
                <span style={{
                  padding: '0.5rem 1.2rem', borderRadius: '0.75rem',
                  border: '2px dashed var(--border)', color: 'var(--text-muted)',
                  fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)',
                }}>___</span>
              )}
            </div>

            {/* Undo button */}
            {placed.length > 0 && !feedback && (
              <button data-testid="undo-button" onClick={handleUndo} style={{
                marginTop: '0.6rem', padding: '0.5rem 1.25rem', borderRadius: '0.75rem',
                background: 'white', border: '2px solid var(--border)', borderBottom: '4px solid var(--border)',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1rem', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                fontFamily: 'Fredoka', touchAction: 'manipulation',
              }}>
                <Undo2 size={18} /> Undo
              </button>
            )}
          </div>

          {/* Wrong answer — hint button (not auto-revealed) */}
          {wrongWord && !feedback && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {!showHint ? (
                <button data-testid="hint-button" onClick={() => { setShowHint(true); playSound('click'); }} style={{
                  padding: '0.4rem 1rem', borderRadius: '2rem',
                  background: 'rgba(245,159,0,0.08)', border: '2px solid rgba(245,159,0,0.25)',
                  color: '#F59F00', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 700,
                  fontFamily: 'Fredoka', display: 'flex', alignItems: 'center', gap: '0.4rem',
                  touchAction: 'manipulation',
                }}>
                  <Lightbulb size={16} /> Need a hint?
                </button>
              ) : (
                <div data-testid="hint-revealed" style={{
                  padding: '0.4rem 1rem', borderRadius: '2rem',
                  background: 'rgba(245,159,0,0.06)', border: '1.5px solid rgba(245,159,0,0.15)',
                  fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700, color: '#888',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}>
                  <Lightbulb size={16} color="#F59F00" />
                  Next: <span style={{ color: '#4C6EF5' }}>{wrongWord}</span>
                </div>
              )}
            </div>
          )}

          {/* Sentence complete feedback */}
          {feedback === 'complete' && (
            <div data-testid="complete-feedback" style={{
              fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)', color: 'var(--success)', fontWeight: 700, fontFamily: 'Fredoka',
              background: 'rgba(47,158,68,0.08)', padding: '0.6rem 1.5rem', borderRadius: '1rem',
              border: '2px solid rgba(47,158,68,0.15)',
              animation: 'popIn 0.3s ease-out',
            }}>
              {currentItem.completed_sentence}
            </div>
          )}

          {/* Available word tiles */}
          <div data-testid="word-tiles" style={{
            display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center',
            maxWidth: 900, padding: '0.5rem',
          }}>
            {available.map((word, idx) => (
              <button key={`${word}-${idx}`} data-testid={`bat-word-${idx}`}
                onClick={() => handleWordTap(word, idx)} disabled={!!feedback}
                style={{
                  padding: '0.75rem 1.5rem', borderRadius: '1rem',
                  background: 'white', border: '2px solid rgba(0,0,0,0.08)',
                  borderBottom: '5px solid var(--border)',
                  color: 'var(--text-primary)',
                  fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', fontWeight: 700, fontFamily: 'Fredoka',
                  cursor: feedback ? 'default' : 'pointer', touchAction: 'manipulation',
                  boxShadow: '0 3px 12px rgba(0,0,0,0.06)',
                  transition: 'transform 0.1s',
                  opacity: feedback ? 0.6 : 1,
                }}
                onMouseDown={e => { if (!feedback) e.currentTarget.style.transform = 'translateY(3px)'; }}
                onMouseUp={e => e.currentTarget.style.transform = ''}
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
